import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useSearchParams} from '../../../router/compat';
import {Link} from 'react-router-dom';
import {useTickets} from '../hooks/useTickets';
import {useDeleteTicket} from '../hooks/useDeleteTicket';
import {
	TicketListQuery,
	buildTicketListVariables,
	resolveMeAssignee,
	ME_SENTINEL,
	useTicketListFetchKey,
} from '../hooks/useTicketListQuery';
import type {useTicketListQuery_tickets$key} from '../hooks/__generated__/useTicketListQuery_tickets.graphql';
import type {useTicketListQueryQuery$data} from '../hooks/__generated__/useTicketListQueryQuery.graphql';
import {ForecastQueryRenderer, commitTicketingMutation} from '../../../graphql/forecastRelay';
import UpdateTicketMutation from '../../../../../mutations/ticketing/tickets/updateTicketMutation';
import {useColumnVisibility} from '../hooks/useColumnVisibility';
import {useDefaultFilters} from '../hooks/useDefaultFilters';
import {useAuth} from '../../auth/context/AuthContext';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';
import {useMentionUsers} from '../hooks/useMentionUsers';
import {useBulkAssign} from '../hooks/useBulkAssign';
import {useFilterDrawer, type DrawerFilterState} from '../hooks/useFilterDrawer';
import {TicketListSkeleton} from '../components/TicketListSkeleton';
import {TicketListEmpty} from '../components/TicketListEmpty';
import {TicketListTable} from '../components/TicketListTable';
import {ColumnsToggle} from '../components/ColumnsToggle';
import {LoadMoreButton} from '../components/LoadMoreButton';
import {ViewToggle, type TicketView} from '../components/ViewToggle';
import {BoardView} from '../components/board/BoardView';
import {BulkActionBar} from '../components/BulkActionBar';
import {BulkAssignModal} from '../components/BulkAssignModal';
import {CreateTicketModal} from '../components/CreateTicketModal';
import {DeleteTicketModal} from '../components/DeleteTicketModal';
import {GlobalSearch} from '../components/GlobalSearch';
import {FilterDrawer} from '../components/FilterDrawer';
import type {ActiveFilters} from '../components/FilterBar';
import type {FilterOption} from '../constants/filterOptions';
import {UNASSIGNED_OPTION} from '../constants/filterOptions';
import type {ColumnKey} from '../constants/listColumns';
import {countActiveFilterFields} from '../utils/countActiveFilterFields';
import {hasMinRole} from '../../auth/utils/hasMinRole';
import {scopeBoardTickets} from '../utils/scopeBoardTickets';
import {useEmbedded} from '../../../EmbeddedContext';
import {TICKET_PERMS, hasTicketPerm} from '../../auth/utils/ticketPerms';
import {useOpenCreateFromQuery} from './useOpenCreateFromQuery';

const QuickFilterButton = styled.button<{$active: boolean}>`
	border-radius: ${p => p.theme.ticketing.radius.full};
	padding: 8px 12px;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	transition: color 0.15s, background-color 0.15s, border-color 0.15s;
	cursor: pointer;
	background: ${p => (p.$active ? '#4f46e5' : '#ffffff')};
	color: ${p => (p.$active ? '#ffffff' : '#475569')};
	border: ${p => (p.$active ? '0' : '1px solid #e2e8f0')};
	&:hover {
		background: ${p => (p.$active ? '#4f46e5' : '#f8fafc')};
	}
	@media (min-width: 640px) {
		padding-top: 4px;
		padding-bottom: 4px;
	}
`;

function QuickFilterPill({label, active, onClick}: {label: string; active: boolean; onClick: () => void}) {
	return (
		<QuickFilterButton type="button" onClick={onClick} $active={active}>
			{label}
		</QuickFilterButton>
	);
}

const ErrorBox = styled.div`
	border-radius: 6px;
	border: 1px solid #fecaca;
	background: #fef2f2;
	padding: 16px;
`;
const ErrorText = styled.p`
	font: ${p => p.theme.ticketing.font.body2};
	color: #b91c1c;
	margin: 0;
`;
const RetryButton = styled.button`
	margin-top: 12px;
	border-radius: 6px;
	background: #dc2626;
	padding: 6px 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	border: 0;
	cursor: pointer;
	&:hover {
		background: #b91c1c;
	}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #ef4444;
	}
`;

const BulkActionBarWrap = styled.div`
	margin-top: 12px;
`;
const ListSection = styled.section`
	margin-top: 16px;
`;
const AgentScopeBanner = styled.div`
	margin-bottom: 12px;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
	padding: 8px 16px;
	font-size: 14px;
	line-height: 20px;
	color: #334155;
`;
const LoadMoreWrap = styled.div`
	margin-top: 16px;
	display: flex;
	justify-content: center;
`;
const PageMain = styled.main`
	padding: 24px;
`;
const HeaderRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	@media (min-width: 640px) {
		flex-direction: row;
		align-items: flex-start;
		justify-content: space-between;
	}
`;
const PageTitle = styled.h1`
	font: ${p => p.theme.ticketing.font.heading1};
	color: #0f172a;
	margin: 0;
`;
const HeaderActions = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	@media (min-width: 640px) {
		gap: 12px;
	}
`;
const SearchWrap = styled.div`
	position: relative;
	width: 100%;
	@media (min-width: 640px) {
		width: auto;
	}
`;
const FilterButton = styled.button<{$active: boolean}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 6px;
	padding: 8px 12px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	cursor: pointer;
	border: 1px solid ${p => (p.$active ? '#6366f1' : '#e2e8f0')};
	background: ${p => (p.$active ? '#eef2ff' : '#ffffff')};
	color: ${p => (p.$active ? '#4338ca' : '#334155')};
	&:hover {
		background: ${p => (p.$active ? '#eef2ff' : '#f8fafc')};
	}
`;
const IconSvg = styled.svg`
	width: 16px;
	height: 16px;
`;
const FilterBadge = styled.span`
	margin-left: 4px;
	display: inline-flex;
	height: 20px;
	min-width: 1.25rem;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	background: #4f46e5;
	padding: 0 6px;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	color: #ffffff;
`;
const SettingsLink = styled(Link)`
	display: block;
	border-radius: 6px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 8px;
	color: #64748b;
	&:hover {
		background: #f8fafc;
	}
`;
const NewTicketButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	border-radius: 6px;
	background: #4f46e5;
	padding: 8px 16px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #ffffff;
	border: 0;
	cursor: pointer;
	&:hover {
		background: #4338ca;
	}
`;
const QuickFilters = styled.div<{$dimmed: boolean}>`
	margin-top: 16px;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	${p => (p.$dimmed ? 'opacity: 0.5; pointer-events: none;' : '')}
`;
const QuickFilterGroupLabel = styled.span`
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	letter-spacing: 0.025em;
	color: #64748b;
`;
const QuickFilterLabel = styled.span`
	font-size: 12px;
	line-height: 16px;
	color: #64748b;
`;
const Divider = styled.span`
	color: #cbd5e1;
`;
const DividerSpaced = styled.span`
	margin-left: 12px;
	color: #cbd5e1;
`;

interface ErrorBoundaryState {
	error: Error | null;
}

class TicketListErrorBoundary extends React.Component<{children: React.ReactNode; onRetry: () => void}, ErrorBoundaryState> {
	override state: ErrorBoundaryState = {error: null};
	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {error};
	}
	override render() {
		if (this.state.error) {
			return (
				<ErrorBox role="alert">
					<ErrorText>{this.state.error.message}</ErrorText>
					<RetryButton
						onClick={() => {
							this.setState({error: null});
							this.props.onRetry();
						}}
					>
						Retry
					</RetryButton>
				</ErrorBox>
			);
		}
		return this.props.children;
	}
}

interface ContentProps {
	view: TicketView;
	queryRef: useTicketListQuery_tickets$key;
	sortField: string | undefined;
	sortOrder: 'asc' | 'desc' | undefined;
	onRefetch: () => void;
	onClearFilters: () => void;
	onSort: (field: string) => void;
	// Receives the ticket's GLOBAL id (resolved from `items` at the child
	// boundary below), so the parent can commit the mutation with it as-is.
	onAssigneeChange: (globalTicketId: string, userId: string | null) => void;
	onAssigneeOptionsChange: (opts: FilterOption[]) => void;
	visibleColumns: ReadonlySet<ColumnKey>;
}

function TicketListContent({
	view,
	queryRef,
	sortField,
	sortOrder,
	onRefetch,
	onClearFilters,
	onSort,
	onAssigneeChange,
	onAssigneeOptionsChange,
	visibleColumns,
}: ContentProps) {
	const {user} = useAuth();
	const {canAssign, canManage} = useTicketPerms();
	const {users} = useMentionUsers();

	const {items, hasNextPage, isLoadingNext, loadNext} = useTickets(queryRef);

	// Delete
	const [deleteTarget, setDeleteTarget] = useState<{id: string; slug: string} | null>(null);
	const {submit: deleteTicket, loading: deleting} = useDeleteTicket();

	const handleDeleteRequest = useCallback(
		(ticketId: string) => {
			const ticket = items.find(t => t.id === ticketId);
			setDeleteTarget({id: ticket?.globalId ?? ticketId, slug: ticket?.slug ?? ''});
		},
		[items]
	);

	// The table leaf hands back the row's RAW id; resolve the GLOBAL id from
	// `items` before delegating to the parent, which commits the mutation.
	const handleAssigneeChange = useCallback(
		(ticketId: string, userId: string | null) => {
			const target = items.find(t => t.id === ticketId);
			onAssigneeChange(target?.globalId ?? ticketId, userId);
		},
		[items, onAssigneeChange]
	);

	const handleDeleteConfirm = useCallback(() => {
		if (!deleteTarget) return;
		deleteTicket(deleteTarget.id).then(
			() => {
				setDeleteTarget(null);
				onRefetch();
			},
			() => {
				setDeleteTarget(null);
			}
		);
	}, [deleteTarget, deleteTicket, onRefetch]);

	// Bulk selection
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [showAssignModal, setShowAssignModal] = useState(false);
	const {status: bulkStatus, results: bulkResults, assign, reset: resetBulk} = useBulkAssign();

	const handleToggleSelect = useCallback((ticketId: string) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(ticketId)) next.delete(ticketId);
			else next.add(ticketId);
			return next;
		});
	}, []);

	const handleToggleAll = useCallback(() => {
		setSelectedIds(prev => {
			const allSelected = items.every(t => prev.has(t.id));
			return allSelected ? new Set() : new Set(items.map(t => t.id));
		});
	}, [items]);

	const handleBulkAssignConfirm = useCallback(
		async (userId: string | null) => {
			await assign(Array.from(selectedIds), userId);
			onRefetch();
		},
		[selectedIds, assign, onRefetch]
	);

	const handleBulkAssignDone = useCallback(() => {
		setShowAssignModal(false);
		setSelectedIds(new Set());
		resetBulk();
	}, [resetBulk]);

	// Derive and sync assignee options to shell (for filter bar)
	const derivedAssigneeOptions = useMemo<FilterOption[]>(() => {
		const seen = new Map<string, string>();
		for (const ticket of items) {
			if (ticket.assignee_id && ticket.assignee_name && !seen.has(ticket.assignee_id)) {
				seen.set(ticket.assignee_id, ticket.assignee_name);
			}
		}
		const userOptions = Array.from(seen.entries()).map(([id, label]) => ({id, label}));
		return [UNASSIGNED_OPTION, ...userOptions];
	}, [items]);

	useEffect(() => {
		onAssigneeOptionsChange(derivedAssigneeOptions);
		// We intentionally only re-run when derivedAssigneeOptions reference changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [derivedAssigneeOptions]);

	return (
		<>
			{view === 'list' && canAssign && selectedIds.size > 0 && (
				<BulkActionBarWrap>
					<BulkActionBar
						selectedCount={selectedIds.size}
						onAssign={() => setShowAssignModal(true)}
						onClear={() => setSelectedIds(new Set())}
						disabled={bulkStatus === 'loading'}
					/>
				</BulkActionBarWrap>
			)}

			<ListSection>
				{view === 'board' && user ? (
					<>
						{user.role === 'agent' && (
							<AgentScopeBanner role="status">Showing your assigned tickets only.</AgentScopeBanner>
						)}
						<BoardView
							tickets={scopeBoardTickets(items, user.role, user.id)}
							me={{sub: user.id, role: user.role}}
							canManage={canManage}
							hasMore={hasNextPage}
							onMutate={onRefetch}
							users={users}
						/>
					</>
				) : items.length === 0 ? (
					<TicketListEmpty onClearFilters={onClearFilters} />
				) : (
					<TicketListTable
						tickets={items}
						sortField={sortField}
						sortOrder={sortOrder}
						onSort={onSort}
						onAssigneeChange={canManage ? handleAssigneeChange : undefined}
						onDelete={canManage ? handleDeleteRequest : undefined}
						selectedIds={canAssign ? selectedIds : undefined}
						onToggleSelect={canAssign ? handleToggleSelect : undefined}
						onToggleAll={canAssign ? handleToggleAll : undefined}
						visibleColumns={visibleColumns}
					/>
				)}

				{view === 'list' && items.length > 0 && hasNextPage && (
					<LoadMoreWrap>
						<LoadMoreButton onClick={() => loadNext(25)} loading={isLoadingNext} />
					</LoadMoreWrap>
				)}
			</ListSection>

			{showAssignModal && (
				<BulkAssignModal
					ticketCount={selectedIds.size}
					users={users}
					loading={bulkStatus === 'loading'}
					results={
						bulkStatus === 'done' ? (bulkResults as Parameters<typeof BulkAssignModal>[0]['results']) : undefined
					}
					onConfirm={handleBulkAssignConfirm}
					onCancel={() => {
						setShowAssignModal(false);
						resetBulk();
					}}
					onDone={handleBulkAssignDone}
				/>
			)}

			<DeleteTicketModal
				open={deleteTarget !== null}
				ticketSlug={deleteTarget?.slug ?? ''}
				loading={deleting}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteTarget(null)}
			/>
		</>
	);
}

export function TicketListRoute() {
	const [searchParams, setSearchParams] = useSearchParams();
	const {user} = useAuth();
	const {canCreate} = useTicketPerms();
	const embedded = useEmbedded();
	const canAdminTickets = hasTicketPerm(user?.ticketPerms ?? [], TICKET_PERMS.ADMIN);
	const canFilterByAnyAssignee = user ? hasMinRole(user.role, 'lead') : false;

	useDefaultFilters();

	const viewParam = searchParams.get('view');
	const view: TicketView = viewParam === 'board' ? 'board' : 'list';

	useEffect(() => {
		if (view !== 'board' || !searchParams.has('status')) return;
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				return updated;
			},
			{replace: true}
		);
	}, [searchParams, setSearchParams, view]);

	const filters: ActiveFilters = {
		status: searchParams.getAll('status'),
		priority: searchParams.getAll('priority'),
		assignee_id: searchParams.getAll('assignee_id'),
		label_id: searchParams.getAll('label_id'),
		requester_id: searchParams.getAll('requester_id'),
		created_after: searchParams.get('created_after') ?? '',
		created_before: searchParams.get('created_before') ?? '',
		updated_after: searchParams.get('updated_after') ?? '',
		updated_before: searchParams.get('updated_before') ?? '',
	};

	const activeFilterCount = countActiveFilterFields(filters);
	const drawerFiltersActive = searchParams.get('filter_source') === 'drawer' && activeFilterCount > 0;

	const sortField = searchParams.get('sort') ?? undefined;
	const sortOrder = (searchParams.get('order') ?? undefined) as 'asc' | 'desc' | undefined;
	const search = searchParams.get('search') ?? undefined;

	function handleSort(field: string) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				const currentField = updated.get('sort');
				const currentOrder = updated.get('order');
				if (currentField === field) {
					updated.set('order', currentOrder === 'asc' ? 'desc' : 'asc');
				} else {
					updated.set('sort', field);
					updated.set('order', 'asc');
				}
				return updated;
			},
			{replace: true}
		);
	}

	function handleFilterChange(next: ActiveFilters) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				const currentView = updated.get('view');
				updated.delete('status');
				updated.delete('priority');
				updated.delete('assignee_id');
				updated.delete('filter_source');
				if (currentView !== 'board') {
					next.status.forEach(v => updated.append('status', v));
				}
				next.priority.forEach(v => updated.append('priority', v));
				next.assignee_id.forEach(v => updated.append('assignee_id', v));

				const allEmpty = next.status.length === 0 && next.priority.length === 0 && next.assignee_id.length === 0;
				if (allEmpty && currentView !== 'board') {
					updated.set('view', 'all');
				} else if (!allEmpty && currentView !== 'board') {
					updated.delete('view');
				}

				return updated;
			},
			{replace: true}
		);
	}

	function handleClearFilters() {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				updated.delete('priority');
				updated.delete('assignee_id');
				updated.delete('label_id');
				updated.delete('requester_id');
				updated.delete('created_after');
				updated.delete('created_before');
				updated.delete('updated_after');
				updated.delete('updated_before');
				updated.delete('search');
				updated.delete('filter_source');
				updated.set('view', 'all');
				return updated;
			},
			{replace: true}
		);
	}

	function handleViewChange(next: TicketView) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				if (next === 'board') {
					updated.set('view', 'board');
					updated.delete('status');
				} else {
					updated.delete('view');
				}
				return updated;
			},
			{replace: true}
		);
	}

	const {fetchKey, refetch} = useTicketListFetchKey();

	const [showCreateModal, setShowCreateModal] = useState(false);

	// ACC-506: open the create modal when arrived at via the top-nav
	// "Create → Ticket" item (`/tickets?create=1`).
	useOpenCreateFromQuery(canCreate, () => setShowCreateModal(true));

	// refetchOnFocus for board view
	useEffect(() => {
		if (view !== 'board') return;
		const onFocus = () => refetch();
		const onVisible = () => {
			if (document.visibilityState === 'visible') refetch();
		};
		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, [view, refetch]);

	// `ticketId` is the GLOBAL id (resolved at the child boundary in
	// TicketListContent). `userId` is a global UserSuggestion.id or null to clear.
	const handleAssigneeChange = useCallback(
		(ticketId: string, userId: string | null) => {
			commitTicketingMutation(
				UpdateTicketMutation,
				{input: {id: ticketId, assigneeId: userId}},
				() => refetch(),
				() => refetch()
			);
		},
		[refetch]
	);

	const [, setAssigneeOptions] = useState<FilterOption[]>([UNASSIGNED_OPTION]);
	const columns = useColumnVisibility();

	const drawer = useFilterDrawer();
	const [savedFiltersFetchKey, setSavedFiltersFetchKey] = useState(0);

	function handleOpenDrawer() {
		drawer.open({
			status: filters.status,
			priority: filters.priority,
			assignee_id: filters.assignee_id,
			label_id: filters.label_id,
			requester_id: filters.requester_id,
			created_after: filters.created_after,
			created_before: filters.created_before,
			updated_after: filters.updated_after,
			updated_before: filters.updated_before,
		});
	}

	function handleDrawerApply(draft: DrawerFilterState) {
		setSearchParams(
			prev => {
				const updated = new URLSearchParams(prev);
				updated.delete('status');
				updated.delete('priority');
				updated.delete('assignee_id');
				updated.delete('label_id');
				updated.delete('requester_id');
				updated.delete('created_after');
				updated.delete('created_before');
				updated.delete('updated_after');
				updated.delete('updated_before');

				draft.status.forEach(v => updated.append('status', v));
				draft.priority.forEach(v => updated.append('priority', v));
				draft.assignee_id.forEach(v => updated.append('assignee_id', v));
				draft.label_id.forEach(v => updated.append('label_id', v));
				draft.requester_id.forEach(v => updated.append('requester_id', v));
				if (draft.created_after) updated.set('created_after', draft.created_after);
				if (draft.created_before) updated.set('created_before', draft.created_before);
				if (draft.updated_after) updated.set('updated_after', draft.updated_after);
				if (draft.updated_before) updated.set('updated_before', draft.updated_before);

				if (countActiveFilterFields(draft) > 0) {
					updated.set('filter_source', 'drawer');
				} else {
					updated.delete('filter_source');
				}

				return updated;
			},
			{replace: true}
		);
		drawer.close();
		refetch();
	}

	return (
		<PageMain>
			{/* Page header row */}
			<HeaderRow>
				<div>
					<PageTitle>All tickets</PageTitle>
				</div>
				<HeaderActions>
					<SearchWrap>
						<GlobalSearch />
					</SearchWrap>
					<FilterButton type="button" onClick={handleOpenDrawer} $active={drawerFiltersActive}>
						<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
							/>
						</IconSvg>
						Filter
						{drawerFiltersActive && activeFilterCount > 0 && (
							<FilterBadge data-testid="filter-badge" aria-label={`${activeFilterCount} active filters`}>
								{activeFilterCount}
							</FilterBadge>
						)}
					</FilterButton>
					<ViewToggle value={view} onChange={handleViewChange} />
					{view === 'list' && <ColumnsToggle isVisible={columns.isVisible} onToggle={columns.toggle} />}
					{!embedded && canAdminTickets && (
						<SettingsLink to="/tickets/settings" aria-label="Ticket settings" title="Ticket settings">
							<IconSvg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
								/>
								<circle cx="12" cy="12" r="3" />
							</IconSvg>
						</SettingsLink>
					)}
					{canCreate && (
						<NewTicketButton type="button" onClick={() => setShowCreateModal(true)}>
							+ New ticket
						</NewTicketButton>
					)}
				</HeaderActions>
			</HeaderRow>

			{/* Quick filters */}
			<QuickFilters data-testid="quick-filters" $dimmed={drawerFiltersActive}>
				<QuickFilterGroupLabel>Quick filters</QuickFilterGroupLabel>
				<Divider>|</Divider>
				<QuickFilterLabel>Priority:</QuickFilterLabel>
				<QuickFilterPill
					label="All"
					active={filters.priority.length === 0}
					onClick={() => handleFilterChange({...filters, priority: []})}
				/>
				<QuickFilterPill
					label="Urgent"
					active={filters.priority.includes('p0')}
					onClick={() =>
						handleFilterChange({
							...filters,
							priority: filters.priority.includes('p0')
								? filters.priority.filter(p => p !== 'p0')
								: [...filters.priority, 'p0'],
						})
					}
				/>
				<QuickFilterPill
					label="High"
					active={filters.priority.includes('p1')}
					onClick={() =>
						handleFilterChange({
							...filters,
							priority: filters.priority.includes('p1')
								? filters.priority.filter(p => p !== 'p1')
								: [...filters.priority, 'p1'],
						})
					}
				/>
				<QuickFilterPill
					label="Medium"
					active={filters.priority.includes('p2')}
					onClick={() =>
						handleFilterChange({
							...filters,
							priority: filters.priority.includes('p2')
								? filters.priority.filter(p => p !== 'p2')
								: [...filters.priority, 'p2'],
						})
					}
				/>
				<QuickFilterPill
					label="Low"
					active={filters.priority.includes('p3')}
					onClick={() =>
						handleFilterChange({
							...filters,
							priority: filters.priority.includes('p3')
								? filters.priority.filter(p => p !== 'p3')
								: [...filters.priority, 'p3'],
						})
					}
				/>
				<DividerSpaced>|</DividerSpaced>
				<QuickFilterLabel>Assignee:</QuickFilterLabel>
				{canFilterByAnyAssignee && (
					<QuickFilterPill
						label="Anyone"
						active={filters.assignee_id.length === 0}
						onClick={() => handleFilterChange({...filters, assignee_id: []})}
					/>
				)}
				<QuickFilterPill
					label="Me"
					active={filters.assignee_id.includes(ME_SENTINEL)}
					onClick={() => handleFilterChange({...filters, assignee_id: [ME_SENTINEL]})}
				/>
				<QuickFilterPill
					label="Unassigned"
					active={filters.assignee_id.includes('__unassigned__')}
					onClick={() => handleFilterChange({...filters, assignee_id: ['__unassigned__']})}
				/>
			</QuickFilters>

			<TicketListErrorBoundary key={fetchKey} onRetry={refetch}>
				<ForecastQueryRenderer
					query={TicketListQuery}
					variables={buildTicketListVariables({
						first: view === 'board' ? 100 : 25,
						filters: {...filters, assignee_id: resolveMeAssignee(filters.assignee_id, user?.id)},
						sort: sortField,
						order: sortOrder,
						search,
					})}
					customLoader={() => <TicketListSkeleton />}
					redirectIfNotLoggedIn
					render={(relayProps: useTicketListQueryQuery$data | null) => {
						if (!relayProps || !relayProps.viewer) return null;
						return (
							<TicketListContent
								view={view}
								queryRef={relayProps.viewer}
								sortField={sortField}
								sortOrder={sortOrder}
								onRefetch={refetch}
								onClearFilters={handleClearFilters}
								onSort={handleSort}
								onAssigneeChange={handleAssigneeChange}
								onAssigneeOptionsChange={setAssigneeOptions}
								visibleColumns={columns.visible}
							/>
						);
					}}
				/>
			</TicketListErrorBoundary>

			<React.Suspense fallback={null}>
				<FilterDrawer
					isOpen={drawer.isOpen}
					draft={drawer.draft}
					onClose={drawer.close}
					onApply={handleDrawerApply}
					onUpdateDraft={drawer.updateDraft}
					onClearDraft={drawer.clearDraft}
					savedFiltersFetchKey={savedFiltersFetchKey}
					onSavedFiltersRefetch={() => setSavedFiltersFetchKey(k => k + 1)}
					canFilterByAssignee={canFilterByAnyAssignee}
				/>
			</React.Suspense>

			<CreateTicketModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onCreated={() => {
					setShowCreateModal(false);
					refetch();
				}}
			/>
		</PageMain>
	);
}
